import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout noFooter>
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center p-8 md:p-16 bg-linen">
        <div className="w-full max-w-md">
          {sent ? (
            <div data-testid="forgot-sent" className="text-center">
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-mustard border-2 border-black flex items-center justify-center">
                <Mail className="w-6 h-6 text-black" strokeWidth={1.5} />
              </div>
              <p className="qld-overline mb-3">check your inbox</p>
              <h1 className="font-serif text-4xl md:text-5xl text-wine mb-3 leading-tight">
                If that email's on file, a link's on the way.
              </h1>
              <p className="text-ink/70 mb-8">
                The link expires in 15 minutes. Can't see it? Check your spam folder or request another.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-resend-btn"
                className="lif-btn lif-btn-cream"
              >
                Send another link
              </button>
              <p className="text-sm text-muted text-center mt-8">
                <Link to="/login" className="qld-link" data-testid="forgot-back-login">Back to log in</Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-4xl md:text-5xl text-wine mb-2">Forgot your password?</h1>
              <p className="text-wine/70 mb-10">
                Enter the email you signed up with and we'll send a reset link.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="qld-input"
                    placeholder="you@example.com"
                    autoFocus
                  />
                </div>
                {err && <p data-testid="forgot-error" className="text-wine text-sm">{err}</p>}
                <button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="qld-btn-primary w-full"
                >
                  {busy ? "Sending…" : <>Send reset link <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
                </button>
              </form>
              <p className="text-sm text-muted text-center mt-8">
                Remembered it? <Link to="/login" className="qld-link" data-testid="forgot-to-login">Back to log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
